import { splitOf } from '../../helpers/lengthSplit';
import { CAPTION_H, BAR_H, BAR_TRACK } from './cellMetrics';

/**
 * How a lot's length is divided between orders and stock.
 *
 * The planned length is one number that answers two different questions, and
 * only one of them is actionable: how much is already somebody's, and how much
 * is still free to promise. A planner hunting for reservable metres was reading
 * that second figure off a row of per-client badges and doing the subtraction
 * in their head.
 *
 * The bar is there so it reads without arithmetic — a glance says "mostly
 * spoken for" or "mostly free" before any digit is parsed. Free metres are
 * green because they are the ones you can do something with.
 *
 * One derivation, used by both tables that show this: the launch split when the
 * job has one, the allocations when it does not. Two screens computing "free"
 * two ways would eventually disagree about the same product.
 */

const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmt = n => Math.round(num(n)).toLocaleString();

const LengthSplit = ({ job, showTotal = true }) => {
  const { total, toOrders, toStock } = splitOf(job);
  if (total <= 0) return <span className="text-body-tertiary">—</span>;

  const pct = Math.round((toOrders / total) * 100);

  return (
    <div className="d-flex flex-column gap-1" style={{ minWidth: 130 }}>
      {/* Same caption height as the Phases column next door — the bar's y IS
          the caption's height, so the two columns are only on one axis if they
          agree about it. `CAPTION_H` is the single number. Reported 2026-10-29:
          "chiar nu-mi place ca sunt descentrate". */}
      {showTotal && (
        <span
          className="fw-semibold fs-9 d-flex align-items-center"
          style={{ height: CAPTION_H }}
        >
          {fmt(total)} m
        </span>
      )}

      {/* The `border` made this 6px box hold a 4px colour band, so it read a
          pixel low and a third lighter than the Phases bar beside it — two bars
          of different weight on almost the same axis, which is what
          "descentrate" was describing. Same track and fill as the other column
          now: no border, full-height fill. */}
      <div
        className="d-flex rounded overflow-hidden"
        style={{ height: BAR_H, background: BAR_TRACK }}
        title={`${fmt(toOrders)} m promised to orders · ${fmt(
          toStock
        )} m free to promise`}
      >
        <div className="bg-info" style={{ width: `${pct}%` }} />
        <div className="bg-success" style={{ width: `${100 - pct}%` }} />
      </div>

      <span className="fs-10 white-space-nowrap">
        {toOrders > 0 && (
          <span className="text-info fw-semibold">{fmt(toOrders)} order</span>
        )}
        {toOrders > 0 && toStock > 0 && (
          <span className="text-body-tertiary"> · </span>
        )}
        {toStock > 0 && (
          <span className="text-success fw-semibold">{fmt(toStock)} free</span>
        )}
        {toOrders <= 0 && toStock <= 0 && (
          <span className="text-body-tertiary">—</span>
        )}
      </span>
    </div>
  );
};

export default LengthSplit;
